import { Duration, milliseconds } from 'date-fns';
import pako from 'pako';

interface CacheEntry<TValue = any> {
  value: TValue;
  expires: number;
}

/**
 * A cache provider.
 */
export interface CacheProvider<TKey = any, TValue = any> extends Map<
  TKey,
  TValue
> {}

export type CacheStorageType = 'local' | 'session';

/**
 * A cache provider that stores data in local or session storage.
 */
export class PersistentCacheProvider<TKey, TValue> implements CacheProvider<
  TKey,
  TValue
> {
  private readonly storageKey: string;
  private readonly storage: Storage;
  private readonly map: Map<TKey, TValue>;
  private readonly enableCompression: boolean;

  constructor(
    storageKey: string,
    storageType: CacheStorageType = 'local',
    enableCompression = true,
  ) {
    this.storageKey = `$cache_${storageKey}`;
    this.storage = storageType === 'local' ? localStorage : sessionStorage;
    this.map = this.getMapFromStorage();
    this.enableCompression = enableCompression;
  }

  /**
   * @inheritdoc
   */
  get size(): number {
    return this.map.size;
  }

  /**
   * @inheritdoc
   */
  get [Symbol.toStringTag](): string {
    return 'StorageCacheProvider';
  }

  /**
   * @inheritdoc
   */
  clear(): void {
    this.map.clear();
    this.updateStorage();
  }

  /**
   * @inheritdoc
   */
  delete(key: TKey): boolean {
    const deleted = this.map.delete(key);
    if (deleted) {
      this.updateStorage();
    }
    return deleted;
  }

  /**
   * @inheritdoc
   */
  forEach(
    callbackfn: (value: TValue, key: TKey, map: Map<TKey, TValue>) => void,
    thisArg?: any,
  ): void {
    this.map.forEach(callbackfn, thisArg);
  }

  /**
   * @inheritdoc
   */
  get(key: TKey): TValue | undefined {
    return this.map.get(key);
  }

  /**
   * @inheritdoc
   */
  has(key: TKey): boolean {
    return this.map.has(key);
  }

  /**
   * @inheritdoc
   */
  set(key: TKey, value: TValue): this {
    this.map.set(key, value);
    this.updateStorage();
    return this;
  }

  /**
   * @inheritdoc
   */
  [Symbol.iterator](): IterableIterator<[TKey, TValue]> {
    return this.map[Symbol.iterator]();
  }

  /**
   * @inheritdoc
   */
  entries(): IterableIterator<[TKey, TValue]> {
    return this.map.entries();
  }

  /**
   * @inheritdoc
   */
  keys(): IterableIterator<TKey> {
    return this.map.keys();
  }

  /**
   * @inheritdoc
   */
  values(): IterableIterator<TValue> {
    return this.map.values();
  }

  /**
   * Returns the map from storage.
   * @private
   */
  private getMapFromStorage(): Map<TKey, TValue> {
    try {
      const storedMap = this.storage.getItem(this.storageKey);
      if (storedMap) {
        let decompressedData = storedMap;
        if (this.enableCompression) {
          decompressedData = pako.inflate(Buffer.from(storedMap, 'base64'), {
            to: 'string',
          });
        }
        const parsedValue = JSON.parse(decompressedData);
        return new Map(parsedValue);
      }
    } catch (error) {
      // Ignore errors
    }
    return new Map<TKey, TValue>();
  }

  /**
   * Updates the storage with the current cache data.
   * @private
   */
  private updateStorage(): void {
    try {
      const serializedValue = JSON.stringify(Array.from(this.map.entries()));
      let dataToStore = serializedValue;
      if (this.enableCompression) {
        dataToStore = Buffer.from(pako.deflate(serializedValue)).toString(
          'base64',
        );
      }
      this.storage.setItem(this.storageKey, dataToStore);
    } catch (error) {
      if (
        error instanceof DOMException &&
        error.name === 'QuotaExceededError'
      ) {
        this.storage.removeItem(this.storageKey);
        this.map.clear();
        this.updateStorage();
      }
    }
  }
}

/**
 * A least-recently-used (LRU) cache.
 */
export class LRUCache<TKey, TValue extends {}> implements CacheProvider<
  TKey,
  TValue
> {
  private readonly _cache: CacheProvider<TKey, CacheEntry<TValue>>;
  private _lastUsedKeys: TKey[] = [];
  private readonly _maxSize: number;
  private readonly _ttl: Duration;

  constructor({
    maxSize = 10,
    ttl = { minutes: 15 },
    backingCache = new Map<TKey, CacheEntry<TValue>>(),
  }: {
    maxSize?: number;
    ttl?: Duration;
    backingCache?: CacheProvider<TKey, CacheEntry<TValue>>;
  }) {
    this._maxSize = maxSize;
    this._ttl = ttl;
    this._cache = backingCache;
  }

  /**
   * Returns the string representation of the cache.
   */
  get [Symbol.toStringTag](): string {
    return this._cache[Symbol.toStringTag];
  }

  /**
   * Returns the number of key-value pairs in the cache.
   */
  get size(): number {
    return this._cache.size;
  }

  /**
   * Returns an iterator over the key-value pairs in the cache.
   */
  [Symbol.iterator](): IterableIterator<[TKey, TValue]> {
    return this.entries();
  }

  /**
   * Returns an iterator over the keys in the cache.
   */
  keys(): IterableIterator<TKey> {
    return this._cache.keys();
  }

  /**
   * Returns an iterator over the values in the cache.
   */
  values(): IterableIterator<TValue> {
    // eslint-disable-next-line func-names
    return function* (this: LRUCache<TKey, TValue>) {
      for (const entry of this._cache.values()) {
        yield entry.value;
      }
    }.call(this);
  }

  /**
   * Removes all key-value pairs from the cache.
   */
  clear(): void {
    this._cache.clear();
    this._lastUsedKeys.length = 0;
  }

  /**
   * Calls callbackFn once for each key-value pair in the cache, in insertion order.
   * @param callbackfn The function to call for each entry.
   * @param thisArg The value to use as `this` when executing `callbackfn`.
   */
  forEach(
    callbackfn: (value: TValue, key: TKey, map: Map<TKey, TValue>) => void,
    thisArg?: any,
  ): void {
    for (const [key, entry] of this._cache.entries()) {
      callbackfn.call(thisArg, entry.value, key, this);
    }
  }

  /**
   * Returns a boolean indicating whether an element with the specified key exists in the cache.
   * @param key The key of the element to check.
   */
  has(key: TKey): boolean {
    const entry = this._cache.get(key);
    return entry !== undefined && entry.expires >= Date.now();
  }

  /**
   * Removes the specified element from the cache.
   * @param key The key of the element to remove.
   */
  delete(key: TKey): boolean {
    const result = this._cache.delete(key);
    if (result) {
      const index = this._lastUsedKeys.indexOf(key);
      this._lastUsedKeys.splice(index, 1);
    }
    return result;
  }

  /**
   * Adds or updates an element with a specified key and value in the cache.
   * @param key The key of the element to add.
   * @param value The value of the element to add.
   */
  set(key: TKey, value: TValue): this {
    if (value === undefined || value === null) {
      return this;
    }
    // If this is not the full version, just update the cache with the existing value
    let cacheValue = value;
    if ((value as any)?.full === undefined && this._cache.has(key)) {
      cacheValue = this._cache.get(key)?.value as TValue;
    }

    const expires = Date.now() + milliseconds(this._ttl);
    this._lastUsedKeys.push(key);
    this._cache.set(key, { value: cacheValue, expires });
    if (this._lastUsedKeys.length > this._maxSize) {
      const keyToRemove = this._lastUsedKeys.shift();
      if (keyToRemove) {
        this._cache.delete(keyToRemove);
      }
    }
    return this;
  }

  /**
   * Returns the value associated with the specified key in the cache.
   * @param key The key of the element to retrieve.
   */
  get(key: TKey): TValue | undefined {
    const entry = this._cache.get(key);
    if (entry !== undefined) {
      if (entry.expires < Date.now()) {
        this._cache.delete(key);
        const index = this._lastUsedKeys.indexOf(key);
        this._lastUsedKeys.splice(index, 1);
        return undefined;
      }
      const index = this._lastUsedKeys.indexOf(key);
      this._lastUsedKeys.splice(index, 1);
      this._lastUsedKeys.push(key);
      return entry.value;
    }
    return undefined;
  }

  /**
   * Returns an iterator over the key-value pairs in the cache.
   */
  entries(): IterableIterator<[TKey, TValue]> {
    this.purgeExpiredEntries();
    // eslint-disable-next-line func-names
    return function* (
      this: LRUCache<TKey, TValue>,
    ): Generator<[TKey, TValue], void> {
      for (const [key, entry] of this._cache.entries()) {
        yield [key, entry.value] as [TKey, TValue];
      }
    }.call(this);
  }

  /**
   * Removes expired entries from the cache.
   */
  private purgeExpiredEntries(): void {
    const currentTime = Date.now();
    for (const [key, entry] of this._cache.entries()) {
      if (entry.expires < currentTime) {
        this._cache.delete(key);
        const index = this._lastUsedKeys.indexOf(key);
        this._lastUsedKeys.splice(index, 1);
      }
    }
  }
}

/**
 * A no-op cache.
 */
export class NoOpCache<TKey, TValue> implements CacheProvider<TKey, TValue> {
  /**
   * Returns the string representation of the cache.
   */
  get [Symbol.toStringTag](): string {
    return 'NoOpCache';
  }

  /**
   * Returns the number of key-value pairs in the cache.
   */
  get size(): number {
    return 0;
  }

  /**
   * Returns an iterator over the key-value pairs in the cache.
   */
  [Symbol.iterator](): IterableIterator<[TKey, TValue]> {
    return [][Symbol.iterator]();
  }

  /**
   * Returns an iterator over the keys in the cache.
   */
  keys(): IterableIterator<TKey> {
    return [][Symbol.iterator]();
  }

  /**
   * Returns an iterator over the values in the cache.
   */
  values(): IterableIterator<TValue> {
    return [][Symbol.iterator]();
  }

  /**
   * Removes all key-value pairs from the cache.
   */
  clear(): void {
    // No-op
  }

  /**
   * Calls callbackFn once for each key-value pair in the cache, in insertion order.
   * @param _callbackfn The function to call for each entry.
   * @param _thisArg The value to use as `this` when executing `callbackfn`.
   */
  forEach(
    _callbackfn: (value: TValue, key: TKey, map: Map<TKey, TValue>) => void,
    _thisArg?: any,
  ): void {
    // No-op
  }

  /**
   * Returns a boolean indicating whether an element with the specified key exists in the cache.
   * @param _key The key of the element to check.
   */
  has(_key: TKey): boolean {
    return false;
  }

  /**
   * Removes the specified element from the cache.
   * @param _key The key of the element to remove.
   */
  delete(_key: TKey): boolean {
    return false;
  }

  /**
   * Adds or updates an element with a specified key and value in the cache.
   * @param _key The key of the element to add.
   * @param _value The value of the element to add.
   */
  set(_key: TKey, _value: TValue): this {
    return this;
  }

  /**
   * Returns the value associated with the specified key in the cache.
   * @param _key The key of the element to retrieve.
   */
  get(_key: TKey): TValue | undefined {
    return undefined;
  }

  /**
   * Returns an iterator over the key-value pairs in the cache.
   */
  entries(): IterableIterator<[TKey, TValue]> {
    return [][Symbol.iterator]();
  }
}
